import {Component, computed, DestroyRef, effect, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {combineLatest, filter, map, switchMap} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {CampaignStoreService, CardResumen} from '../../../campaña/services/campaign-store.service';
import {ReportControlsComponent, Scenario} from '../../components/report-controls/report-controls.component';
import {AnalysisMode} from '../../components/filters-report/reporte-filtros.component';
import {ReportesTitleComponent} from '../../components/header-report/reportes-title.component';
import {EmptyStateComponent} from '@/app/shared/components/empty-state/empty-state.component';
import {CardCampanaComponent} from '@/app/features/campaña/components/card-campana-component/card-campana-component';
import {DonutCardComponent} from '@/app/shared/components/donut-card/donut-card.component';
import {InfoCardComponent} from '@/app/shared/components/info-card/info-card.component';
import {AppIcons} from '@/assets/icons/icons';
import {BarCardComponent} from '@/app/shared/components/bar-card/bar-card.component';
import {MultiDonutCardComponent} from '@/app/shared/components/multi-donut-card/multi-donut-card.component';
import {InitialChartDataService} from "../../services/initial-chart-data.service";
import {DomSanitizer} from '@angular/platform-browser';
import {ReportFiltersStateService} from "../../components/filters-report/service/report-filters-state.service";
import {ReportScenarioComponent} from '@/app/features/reportes/components/report-scenario/report-scenario.component';
import {PdfExportService} from '@/app/shared/services/pdf-export.service';
import {ThemeService} from "@/app/core/services/theme/theme.service";

@Component({
  selector: 'app-guia2-report',
  standalone: true,
  templateUrl: './guia2-report.html',
  styleUrl: './guia2-report.scss',
  imports: [
    AsyncPipe,
    ReportesTitleComponent,
    ReportControlsComponent,
    EmptyStateComponent,
    CardCampanaComponent,
    DonutCardComponent,
    InfoCardComponent,
    BarCardComponent,
    MultiDonutCardComponent,
    ReportScenarioComponent,
  ],
})
export class Guia2ReportComponent {
  protected reportContent = computed(() => this.themeContent()?.reports);
  protected pageData = signal<{ title: string; subTitle: string; }>({title: '', subTitle: ''});
  private themeService = inject(ThemeService);
  protected themeContent = this.themeService.themeContent;

  protected analysisMode: AnalysisMode = 'final-assessments';
  protected hasAppliedFilters = false;
  protected scenarios: Scenario[] = [];
  protected isExporting = false;
  private store: CampaignStoreService = inject(CampaignStoreService);
  private reportFiltersStateService = inject(ReportFiltersStateService);
  private pdfExportService = inject(PdfExportService);
  totalEmployees$ = this.store.totalEmployees$;
  evaluados$ = this.store.respondedCount$;
  conAts$ = this.store.conAtsCount$;

  protected respondedCount$ = this.store.respondedCount$;
  protected respondedCountSignal = toSignal(this.respondedCount$, {initialValue: 0});
  protected hasData = computed(() => this.respondedCountSignal() > 0);
  private sanitizer = inject(DomSanitizer);
  leftCards$ = combineLatest([
    this.store.summaryCards$,
    this.store.percentageCard$,
  ]).pipe(
    map(([cols, pc]) => {
      const activada = cols[0]?.[0] ?? null;
      const diasFin = cols[1]?.[1] ?? null;

      const diasFinGray = diasFin ? {...diasFin, colorIcon: 'gray'} : null;

      const cumplimientoMini: CardResumen | null = pc
        ? {
          titulo: `${pc.percentage}%`,
          subtitulo: 'Nivel de cumplimiento',
          icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.CIRCLE),
          colorIcon: pc.colorIcon,
        }
        : null;

      return [activada, cumplimientoMini, diasFinGray].filter(Boolean) as CardResumen[];
    })
  );
  atsIcon = this.sanitizer.bypassSecurityTrustHtml(AppIcons.ATS);
  private initialData = inject(InitialChartDataService);
  private destroyRef = inject(DestroyRef);
  private readonly selectedCampaignId$ = this.store.selectedCampaignClient$.pipe(
    map(c => c?.publicId ?? null)
  );
  protected readonly campaignId = toSignal(this.selectedCampaignId$, {initialValue: null});
  private campaignIdStream$ = this.selectedCampaignId$.pipe(
    filter((campaignId): campaignId is string => !!campaignId)
  );
  private ageRangeData$ = this.campaignIdStream$.pipe(
    switchMap(campaignId => this.initialData.getAgeRangeDistribution(campaignId))
  );
  private ageRangeSignal = toSignal(this.ageRangeData$, {initialValue: []});
  protected ageRangeBarData = computed(() => {
    return this.ageRangeSignal() ?? [];
  });
  private genderData$ = this.campaignIdStream$.pipe(
    switchMap(campaignId => this.initialData.getGenderDistribution(campaignId))
  );
  private genderSignal = toSignal(this.genderData$, {initialValue: []});
  protected genderDonutData = computed(() => {
    return this.genderSignal() ?? [];
  });

  protected severeTraumaticEventContent = computed(() => this.reportContent()?.severeTraumaticEventContent);
  protected themeColors = computed(() => this.themeService.getTheme()?.colors);

  protected barCardComponent = computed(() => this.reportContent()?.barCardComponent);
  protected multiDonutCardComponent = computed(() => this.reportContent()?.multiDonutCardComponent);

  constructor() {
    this.reportFiltersStateService.clearState();
    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scenarios = [];
        this.hasAppliedFilters = false;
        this.analysisMode = 'final-assessments';
      });

    effect(() => {
      const title = this.reportContent()?.guideTwoReportComponent?.title ?? '';
      this.pageData.set({title: title, subTitle: ''});
    });
  }

  // Contenido de texto para las donas
  protected evaluatedDonutContent = computed(() => {
    const donutContent = this.reportContent()?.donutCardComponent;
    return {
      title: donutContent?.title ?? '',
      primaryLabel: donutContent?.evaluatedLabel ?? '',
      secondaryLabel: donutContent?.notEvaluatedLabel ?? '',
    };
  });

  protected atsDonutContent = computed(() => {
    const donutContent = this.reportContent()?.donutCardComponent;
    return {
      title: donutContent?.severeTraumaticEventTitle ?? '',
      primaryLabel: donutContent?.severeTraumaticEventLabel ?? '',
      secondaryLabel: donutContent?.noSevereTraumaticEventLabel ?? '',
    };
  });

  // Colores para las donas
  protected evaluatedDonutColors = computed(() => {
    const colors = this.themeColors();
    return {
      primary: colors?.colorChartSuccess ?? '#000',
      secondary: colors?.colorChartNeutral ?? '#ccc',
    };
  });

  protected atsDonutColors = computed(() => {
    const colors = this.themeColors();
    return {
      primary: colors?.colorResultVeryHigh ?? '#000',
      secondary: colors?.colorChartNeutral ?? '#ccc',
    };
  });

  removeScenario(id: string) {
    this.scenarios = this.scenarios.filter(s => s.id !== id);
    if (this.scenarios.length === 0) {
      this.hasAppliedFilters = false;
      this.analysisMode = 'final-assessments';
    }
  }

  async exportToPdf() {
    this.isExporting = true;
    // Esperar un poco más para que los gráficos se rendericen completamente
    await new Promise(resolve => setTimeout(resolve, 2000));
    await this.pdfExportService.exportToPdf('.pdf-export', 'reporte-guia2.pdf');
    this.isExporting = false;
  }
}
